import React from "react";
import { Alert, Descriptions } from "antd";
import { DescriptionsProps } from "antd/lib/descriptions";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import moment from "moment";
import { AllocationBreakdown } from "../helpers/shared-interfaces";

interface TreasuryBillRedemptionBreakdownProps {
  data: AllocationBreakdown["data"];
  showExplanations?: boolean;
  // Narrow containers such as the redeem modal need fewer columns.
  column?: DescriptionsProps["column"];
}

const amount = (value?: string | number, precision = 2): string =>
  `GHS ${(parseFloat(String(value ?? 0)) || 0).toFixed(precision)}`;

export const TreasuryBillRedemptionBreakdown: React.FC<
  TreasuryBillRedemptionBreakdownProps
> = ({ data, showExplanations = true, column = { xs: 1, sm: 2, md: 3 } }) => {
  const redemption = data.redemption_data;
  const isPartial = data.redemption_type === "partial";
  // Charges apply to any redemption before maturity; a matured bill is redeemed without them.
  const isEarly = data.timing === "early";
  const redeemingOn = moment(redemption.rediscount_date).format("DD MMM YYYY");
  const maturesOn = moment(data.maturity_date).format("DD MMM YYYY");
  const charges = `A service cost of ${redemption.service_cost_rate}% and a CSD charge of ${redemption.csd_charge_rate}% are applied to the accrued amount.`;

  return (
    <>
      {showExplanations &&
        (isEarly ? (
          <Alert
            message={
              isPartial
                ? "Partial Redemption"
                : "Full Redemption Before Maturity"
            }
            description={
              isPartial
                ? `Redeeming on ${redeemingOn} (before maturity on ${maturesOn}). ${charges} The remaining balance is reinvested until maturity.`
                : `Redeeming everything on ${redeemingOn} (before maturity on ${maturesOn}). ${charges} The customer receives the discounted amount.`
            }
            type="warning"
            icon={<WarningOutlined />}
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            message={
              isPartial ? "Partial Redemption at Maturity" : "Full Redemption"
            }
            description={
              isPartial
                ? `Redeeming at maturity on ${maturesOn} with no charges. The remaining balance is reinvested for another ${redemption.remaining_tenor} days at the same rate.`
                : `Redeeming at maturity on ${maturesOn}. Base amount plus accrued interest with no charges.`
            }
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            style={{ marginBottom: 16 }}
          />
        ))}

      <Descriptions size="small" column={column}>
        <Descriptions.Item label={<b>Accrued Amount</b>}>
          {amount(redemption.accrued_amount)}
        </Descriptions.Item>

        {isEarly && (
          <Descriptions.Item
            label={<b>Service Cost ({redemption.service_cost_rate}%)</b>}
          >
            <span style={{ color: "#cf1322" }}>
              {amount(redemption.service_cost)}
            </span>
          </Descriptions.Item>
        )}

        {isEarly && (
          <Descriptions.Item
            label={<b>CSD Charge ({redemption.csd_charge_rate}%)</b>}
          >
            <span style={{ color: "#cf1322" }}>
              {amount(redemption.csd_charge)}
            </span>
          </Descriptions.Item>
        )}

        {isEarly && (
          <Descriptions.Item label={<b>Net Available</b>}>
            {amount(redemption.net_available_amount)}
          </Descriptions.Item>
        )}

        <Descriptions.Item
          label={<b>{isPartial ? "Redemption Amount" : "Total Redemption"}</b>}
        >
          <span style={{ fontWeight: 600, fontSize: "16px", color: "#52c41a" }}>
            {amount(redemption.total_redemption_amount)}
          </span>
        </Descriptions.Item>

        {isPartial && (
          <Descriptions.Item label={<b>Remaining Balance</b>}>
            <span style={{ fontWeight: 600 }}>
              {amount(redemption.remaining_balance)}
            </span>
          </Descriptions.Item>
        )}

        {isPartial && (
          <Descriptions.Item label={<b>Remaining Tenor</b>}>
            {redemption.remaining_tenor} days
          </Descriptions.Item>
        )}

        <Descriptions.Item label={<b>Rediscount Date</b>}>
          {moment(redemption.rediscount_date).format("DD MMM YYYY")}
        </Descriptions.Item>
      </Descriptions>
    </>
  );
};
